import * as Sentry from "@sentry/react"
import { getAuth, signInAnonymously } from "firebase/auth"
import React, { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AgentView } from "../../components/AgentView"
import { CallShell } from "../../components/layout/CallShell"
import { Loader } from "../../components/loader"
import { SnackbarWithText } from "../../components/snackbar"
import { StorageKeys } from "../../constants"
import { updateMixpanelUser } from "../../external/analytics"
import { prewarmBackgroundProcessor } from "../../lib/backgroundProcessor"
import type { Agent } from "../../types/Agent"
import { initialAgent } from "../../types/Agent"
import { AnalyticsEvents } from "../../types/Analytics"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { isAnonymous, isInIframe } from "../../utils/func"
import {
  ApiError,
  createLivekitRoom,
  getAgent,
  getDefaultAgentId,
  isLocalDev,
  LIVEKIT_ROOM_STORAGE_KEY,
} from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"

export function PreScreenCustomBranding() {
  const [permissionsGranted, setPermissionsGranted] = useState(false)
  const [agentError, setAgentError] = useState<string>("")
  const [agentExists, setAgentExists] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [joinModalOpen, setJoinModalOpen] = useState<boolean>(false)
  const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false)
  const [isCustomBrandingAgent, setIsCustomBrandingAgent] = useState<
    boolean | null
  >(null)

  const brandingResolvedRef = useRef(false)
  const previousIdRef = useRef<string | undefined>(undefined)

  const auth = getAuth()

  const { state } = useLocation()
  const location = useLocation()
  const context = useAppContext()
  const { user, stream, agent, call } = context
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatchContext()
  const isUserAnonymous = isAnonymous()
  const isEmbed = isInIframe()
  const { logAnalyticsEvent } = useMixpanel()

  const agentId = id || getDefaultAgentId
  const showContactForm =
    isCustomBrandingAgent === true &&
    isUserAnonymous &&
    agent?.custom_branding?.enable_contact_form !== false

  useEffect(() => {
    setPermissionsGranted(false)
    sessionStorage.removeItem(LIVEKIT_ROOM_STORAGE_KEY)

    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop())
    }

    dispatch({ type: StateAction.end })
    dispatch({ type: StateAction.setAudioDevice, payload: null })
    dispatch({ type: StateAction.setVideoDevice, payload: null })
  }, [location.pathname, location.search])

  useEffect(() => {
    if (state && user) {
      navigate(`/${state}`)
    }
  }, [state, user])

  useEffect(() => {
    if (user) {
      setLoginModalOpen(false)
    }
  }, [user])

  useEffect(() => {
    if (
      isCustomBrandingAgent !== null &&
      !showContactForm &&
      permissionsGranted &&
      user
    ) {
      setJoinModalOpen(true)
    }
  }, [permissionsGranted, user, showContactForm, isCustomBrandingAgent])

  const fetchAgent = async (agentId: string): Promise<Agent> => {
    const fetchedAgent = await getAgent(agentId)
    setAgentExists(true)
    dispatch({ type: StateAction.setAgent, payload: fetchedAgent })
    return fetchedAgent
  }

  useEffect(() => {
    if (id !== previousIdRef.current) {
      brandingResolvedRef.current = false
      setIsCustomBrandingAgent(null)
      previousIdRef.current = id
    }

    let cancelled = false

    const initialize = async () => {
      try {
        if (!brandingResolvedRef.current) {
          if (!user) {
            const result = await signInAnonymously(auth)
            if (cancelled) return
            if (!result.user) {
              setAgentError("Failed to authenticate user")
              return
            }
          }

          const fetchedAgent = await fetchAgent(agentId)
          if (cancelled) return

          const isCustomBranding =
            !!fetchedAgent.custom_branding || agentId === getDefaultAgentId
          setIsCustomBrandingAgent(isCustomBranding)
          brandingResolvedRef.current = true

          const willShowContactForm =
            isCustomBranding &&
            fetchedAgent.custom_branding?.enable_contact_form !== false

          if (!willShowContactForm && isAnonymous()) {
            localStorage.removeItem(StorageKeys.ANON_EMAIL)
            localStorage.removeItem(StorageKeys.ANON_NAME)
            localStorage.removeItem(StorageKeys.ANON_FIRST_NAME)
            localStorage.removeItem(StorageKeys.ANON_LAST_NAME)
            localStorage.removeItem(StorageKeys.ANON_COMPANY)
            const anonUser = {
              uid:
                getAuth().currentUser?.uid ||
                `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
              name: "Anonymous",
              firstName: "Anonymous",
              lastName: "",
              email: "anonymous@example.com",
            }
            dispatch({ type: StateAction.setUser, payload: anonUser })
            if (!isLocalDev) {
              updateMixpanelUser(anonUser)
            }
          }
        }
      } catch (error) {
        if (cancelled) return
        console.error("Error during initialization:", error)
        Sentry.captureException(error)
        if (
          error instanceof ApiError &&
          (error.status === 400 || error.status === 404)
        ) {
          setAgentExists(false)
          dispatch({ type: StateAction.setAgent, payload: initialAgent })
        } else {
          setAgentExists(false)
          setAgentError(
            "Something went wrong. Please check the link and try again.",
          )
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }
    initialize()

    return () => {
      cancelled = true
    }
  }, [id, user])

  useEffect(() => {
    logAnalyticsEvent(AnalyticsEvents.AgentVisited)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (agent.agent_background_url) {
      prewarmBackgroundProcessor(agent.agent_background_url)
    }
  }, [agent.agent_background_url])

  const onJoinCall = async () => {
    try {
      const roomInfo = await createLivekitRoom(agentId)

      dispatch({
        type: StateAction.setCall,
        payload: {
          ...call,
          id: roomInfo.call_id,
          agentId: agentId,
          startedAt: new Date(),
        },
      })

      navigate(`/rooms/${agentId}`)
    } catch (error) {
      console.error("error joining call", error)
      if (error instanceof ApiError && error.status === 503) {
        setAgentError("All avatars are busy right now. Please try again later.")
      } else if (
        error instanceof ApiError &&
        (error.status === 402 || error.status === 429)
      ) {
        setAgentError(
          "This agent cannot take more calls right now. Please try again later.",
        )
      } else {
        setAgentError("Something went wrong. Please try again.")
      }
    }
  }

  const loginModalProps = {
    open: loginModalOpen,
    onOpen: () => setLoginModalOpen(true),
    onClose: () => setLoginModalOpen(false),
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <CallShell isEmbed={isEmbed}>
      <SnackbarWithText
        open={agentError !== ""}
        severity={SnackbarType.error}
        text={agentError}
        handleClose={() => setAgentError("")}
      />
      <AgentView
        agent={agent}
        permissionsGranted={permissionsGranted}
        setPermissionsGranted={setPermissionsGranted}
        onJoinCall={onJoinCall}
        showContactForm={showContactForm}
        joinMeetingModalProps={{
          open: joinModalOpen,
          onClose: () => setJoinModalOpen(false),
        }}
        loginModalProps={loginModalProps}
        agentExists={agentExists}
      />
    </CallShell>
  )
}
